<template>
  <div class="social-snapshot-widget">
    <!-- Standard page header -->
    <div class="standard-page-header standard-page-header--no-bottom-line social-snapshot-widget__header">
      <h2 class="global-h2 standard-page-header__title">
        <RouterLink
          :to="{
            name: 'SuggestedSocialPosts',
            params: { pageId: props.customerId },
          }"
        >
          Social Snapshots
        </RouterLink>
      </h2>

      <div class="standard-page-header__subtitle-and-link">
        <div class="standard-page-header__subtitle">Captivate your audience with vibrant, weekly content-powered by your adventures and enhanced by your favorite photos.</div>
      </div>
    </div>
    <!-- / Standard page header -->

    <Loader v-if="isLoadingAllManuallyUploadedMedia || isLoadingAllPosts" />

    <div v-else-if="socialMediaPosts?.length" class="social-snapshot-widget__posts-list">
      <SocialPostPreview
        class="social-snapshot-widget__post"
        v-for="socialPost in socialMediaPosts.slice(0, howManyPostsToShow)"
        :key="socialPost?.id"
        :assetIds="socialPost.assetIds"
        :caption="socialPost.caption || ''"
        :isWithTextarea="false"
        :isWithShareAndDislikeButtons="true"
        @clickCustomizeAndShare="
          () => {
            postToEdit = socialPost;
            isEditAndShareSocialPostModalVisible = true;
          }
        "
        @clickDislike="
          () => {
            postIdToDislike = socialPost?.id;
            isDislikeModalVisible = true;
          }
        "
        @updatePostUrl="
          (newUrl, etag) => {
            socialPost.postUrl = newUrl;
            socialPost._etag = etag;
          }
        "
        :fancyboxKey="socialPost?.id"
        :post="socialPost"
      />

      <EditAndShareSocialPostModal
        v-if="isEditAndShareSocialPostModalVisible"
        :postToEdit="postToEdit"
        :customerId="props.customerId"
        @close="isEditAndShareSocialPostModalVisible = false"
        @savePost="() => {}"
      />

      <DislikeFormModal
        v-if="isDislikeModalVisible"
        :pageId="customerId"
        :postId="postIdToDislike"
        @close="isDislikeModalVisible = false"
        @remove="reasonText => dislikeAndRemovePost(postIdToDislike, reasonText)"
      />
    </div>

    <DragAndDropArea v-else class="social-snapshot-widget__drag-and-drop-area" :customerId="props.customerId" :minImagesNumber="minImagesNumber" v-model:imagesList="allManuallyUploadedMedia" />

    <SrpButton
      v-if="socialMediaPosts.length"
      tag="RouterLink"
      fill="outlined"
      class="social-snapshot-widget__generate-post-button"
      :to="{
        name: 'SuggestedSocialPosts',
        params: { pageId: props.customerId },
        hash: `#isOpenGenModal=true`,
      }"
    >
      <template v-if="isSendingGenerateRequest" #icon><LoadingIcon :size="20" /></template>
      Generate more Posts
    </SrpButton>

    <SrpButton
      v-else
      class="social-snapshot-widget__generate-post-button"
      :isDisabled="allManuallyUploadedMedia.length < minImagesNumber || isLoadingAllManuallyUploadedMedia || isSendingGenerateRequest"
      @click="generatePost"
    >
      <template v-if="isSendingGenerateRequest" #icon><LoadingIcon :size="20" /></template>
      Generate Post
    </SrpButton>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from "vue";
import PhotoGalleryRepo from "@repos/PhotoGalleryRepo";

// Components
import SrpButton from "@components/ui/SrpButton.vue";
import Loader from "@components/Loader/Loader.vue";
import DragAndDropArea from "./DragAndDropArea.vue";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";
import SocialPostPreview from "@components/SocialPostPreview/index.vue";

// Types
import { SocialMediaPostData } from "@contracts/shareStop";
import EditAndShareSocialPostModal from "@views/Community/SuggestedSocialPosts/EditAndShareSocialPostModal/index.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import DislikeFormModal from "@views/Community/SuggestedSocialPosts/DislikeFormModal.vue";
import { MetricSender } from "@helpers/MetricSender";

const props = withDefaults(
  defineProps<{
    customerId: string;
  }>(),
  {
    customerId: "",
  }
);

// Post to edit ===============================================================
const postToEdit = ref<SocialMediaPostData | null>(null);

// Toggle dislike modal =======================================================
const postIdToDislike = ref<string>("");
const isDislikeModalVisible = ref<boolean>(false);

// Toggle "Edit and share social post" modal ==================================
const isEditAndShareSocialPostModalVisible = ref<boolean>(false);

// Min images number ==========================================================
const minImagesNumber = 20;

// Load all customer manually uploaded media ==================================
const isLoadingAllManuallyUploadedMedia = ref<boolean>(false);
const allManuallyUploadedMedia = ref<Array<string>>([]);

async function loadMedia() {
  isLoadingAllManuallyUploadedMedia.value = true;
  allManuallyUploadedMedia.value = await PhotoGalleryRepo.getAllCustomerManuallyUploadedMedia(props.customerId);
  isLoadingAllManuallyUploadedMedia.value = false;
}

// How many posts to show =====================================================
const howManyPostsToShow = 3;

// Load manually created posts ================================================
const isLoadingAllPosts = ref<boolean>(true);
const socialMediaPosts = ref<Array<SocialMediaPostData>>([]);

async function loadPosts() {
  isLoadingAllPosts.value = true;

  const postsResult = await ShareStopSummaryRepo.loadLatestWeeklyPosts(props.customerId);
  socialMediaPosts.value = postsResult ?? [];

  isLoadingAllPosts.value = false;
}

// Load posts and media files if needed =======================================
async function loadPostsAndMedia() {
  await loadPosts();

  // Media files are needed only when the Posts list is empty
  if (!socialMediaPosts.value?.length) {
    await loadMedia();
  }
}

onMounted(loadPostsAndMedia);

watch(() => props.customerId, loadPostsAndMedia);

// Generate post ==============================================================
const isSendingGenerateRequest = ref<boolean>(false);

async function generatePost() {
  isSendingGenerateRequest.value = true;
  const results = await ShareStopSummaryRepo.genSocialPosts(props.customerId, allManuallyUploadedMedia.value, "", 2, true, true);
  isSendingGenerateRequest.value = false;

  socialMediaPosts.value = results.results.map(p => p.post);
}

// Dislike post ===============================================================
async function dislikeAndRemovePost(postId: string, reasonText: string) {
  // Check in manually created posts
  const postIndex = socialMediaPosts.value.findIndex(p => p.id === postId);
  if (postIndex !== -1) {
    socialMediaPosts.value.splice(postIndex, 1);
  }

  await ShareStopSummaryRepo.deletePost(props.customerId, postId, reasonText);

  MetricSender.sendMetric("Cust-SocialPost-Delete", { socialPostId: postId });
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Social snapshots widget ====================================================
.social-snapshot-widget {
  &__header {
    margin-bottom: 25px;
  }

  &__posts-list {
    gap: $desktop-wide-grid-gap-width;
    margin-bottom: 20px;
    display: flex;
    flex-wrap: wrap;
  }

  &__post {
    width: calc(33.3% - $desktop-wide-grid-gap-width * 2 / 3);
  }

  &__drag-and-drop-area {
    margin-bottom: 20px;
  }

  &__generate-post-button {
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .social-snapshot-widget {
    &__posts-list {
      gap: $laptop-grid-gap-width;
    }

    &__post {
      width: calc(33.3% - $laptop-grid-gap-width * 2 / 3);
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .social-snapshot-widget {
    &__posts-list {
      gap: $tablet-large-grid-gap-width;
    }

    &__post {
      width: calc(33.3% - $tablet-large-grid-gap-width * 2 / 3);
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .social-snapshot-widget {
    &__posts-list {
      gap: $tablet-grid-gap-width;
    }

    &__post {
      width: calc(50% - $tablet-grid-gap-width * 1 / 2);
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .social-snapshot-widget {
    &__post {
      width: 100%;
    }
  }
}
</style>
